/**
 * AP-35 — Verträge des Projektmanagements, die Backend, ERP und PWA teilen.
 *
 * Die Werte spiegeln die Prisma-Enums in `apps/backend/prisma/schema/`; dieses
 * Paket kennt kein Prisma, deshalb stehen sie hier als Literale. Ein Test im
 * Backend hält beide Listen gleich.
 *
 * Fachliche Quelle: `docs/Arbeitspakete/AP-35/Detail/` (Blätter 1, 2, 4).
 */

/**
 * Lebenslauf des Projekts — **vier Stufen** (Auftraggeber 18.9.2026, AP-35
 * Nachtrag 2). Bis dahin zehn (Anfrage, Angebot, beauftragt, in Arbeit, ruhend,
 * fertiggestellt, abgerechnet, archiviert, verloren, storniert); die Migration
 * `ap35n2_projektstatus` hat den Bestand übergeführt. „Abgerechnet" ist kein
 * Status mehr, sondern ergibt sich aus der Abrechnung; ein Angebot, das nicht
 * kommt, wird storniert.
 */
export const PROJECT_STATUSES = ['PREPARATION', 'ACTIVE', 'COMPLETED', 'CANCELLED'] as const
export type ProjectStatus = (typeof PROJECT_STATUSES)[number]

/**
 * Die zehn Werte vor dem 18.9.2026 — sie stehen noch als Text im Protokoll
 * (`project_approvals.fromStatus/toStatus`) und werden dort beschriftet, nie
 * mehr gesetzt.
 */
export const PROJECT_STATUS_TEXT_BIS_2026_09: Readonly<Record<string, string>> = {
  INQUIRY: 'Anfrage',
  QUOTED: 'Angebot',
  ORDERED: 'beauftragt',
  IN_PROGRESS: 'in Arbeit',
  ON_HOLD: 'ruhend',
  INVOICED: 'abgerechnet',
  ARCHIVED: 'archiviert',
  LOST: 'verloren',
}

/** Detailblatt 1 2.6. */
export const PROJECT_TYPES = ['ORDER', 'COLLECTIVE', 'INTERNAL'] as const
export type ProjectType = (typeof PROJECT_TYPES)[number]

/**
 * AP-35 Phase K — der Lebenslauf im Klartext, für Exporte und Listen. Modusfrei:
 * Kein Statuswort trägt den Begriff Projekt/Auftrag (Detailblatt 1 9.).
 */
export const PROJECT_STATUS_TEXT: Readonly<Record<string, string>> = {
  PREPARATION: 'In Vorbereitung',
  ACTIVE: 'Aktiv',
  COMPLETED: 'Fertiggestellt',
  CANCELLED: 'Storniert',
}

/**
 * Detailblatt 2 4.1 — Lebenslauf der Position. Seit Nachtrag 7 (Auftraggeber
 * 19.9.2026) fünf Stufen wie beim Projekt: „in Arbeit" und „abgerechnet" gibt es
 * nicht mehr — gearbeitet wird an einer freigegebenen Position, was abgerechnet
 * ist, sagt der Reiter Verkauf.
 */
export const PROJECT_POSITION_STATUSES = ['PREPARATION', 'RELEASED', 'BLOCKED', 'COMPLETED', 'CANCELLED'] as const
export type ProjectPositionStatus = (typeof PROJECT_POSITION_STATUSES)[number]

/** Die Beschriftung, modusfrei — für Exporte, Listen und die Oberfläche. */
export const PROJECT_POSITION_STATUS_TEXT: Readonly<Record<ProjectPositionStatus, string>> = {
  PREPARATION: 'In Vorbereitung',
  RELEASED: 'Freigegeben',
  BLOCKED: 'Gesperrt',
  COMPLETED: 'Fertiggestellt',
  CANCELLED: 'Storniert',
}

/**
 * Die Werte vor Nachtrag 7 — sie stehen noch als Text im Protokoll
 * (`project_approvals.fromStatus/toStatus`) und werden dort beschriftet, nie
 * mehr gesetzt.
 */
export const PROJECT_POSITION_STATUS_TEXT_BIS_2026_09: Readonly<Record<string, string>> = {
  PLANNED: 'geplant',
  IN_PROGRESS: 'in Arbeit',
  ON_HOLD: 'angehalten',
  INVOICED: 'abgerechnet',
}

/**
 * Detailblatt 4 4. — Lebenslauf des Arbeitsschritts. `ASSIGNED` („zugeteilt“,
 * AP-35 Nachtrag 9): Der Auftrag ist gedruckt oder einem Mitarbeiter in die
 * PWA geschickt — zwischen „freigegeben“ und „in Arbeit“.
 */
export const PROJECT_WORK_STEP_STATUSES = ['PLANNED', 'READY', 'ASSIGNED', 'IN_PROGRESS', 'PAUSED', 'DONE', 'CANCELLED'] as const
export type ProjectWorkStepStatus = (typeof PROJECT_WORK_STEP_STATUSES)[number]

/**
 * Die fünf Werte, die `projects.status` bis AP-35 Phase B hatte — und die die
 * Integrations-API weiterhin herausgibt. Sie ist ein veröffentlichter Vertrag; ein
 * Fremdsystem, das `ACTIVE` erwartet, soll nach dem Umbau nicht `IN_PROGRESS` lesen.
 */
export type LegacyProjectStatus = 'PLANNED' | 'ACTIVE' | 'PAUSED' | 'COMPLETED' | 'CANCELLED'

/**
 * Neuer Lebenslauf → alter Wert der Schnittstelle. Die Gegenrichtung der
 * Übernahme in der Migration (PLANNED → ORDERED, ACTIVE → IN_PROGRESS,
 * PAUSED → ON_HOLD); die neuen Stufen fallen auf den nächstliegenden alten Wert.
 */
export const LEGACY_PROJECT_STATUS: Readonly<Record<ProjectStatus, LegacyProjectStatus>> = {
  PREPARATION: 'PLANNED',
  ACTIVE: 'ACTIVE',
  COMPLETED: 'COMPLETED',
  CANCELLED: 'CANCELLED',
}

/**
 * Detailblatt 1 3.1, Spalte „In Auswahlfeldern anderer Module": In diesen
 * Zuständen erscheint ein Projekt in Auswahllisten (Belege, alte Buchungsmaske).
 */
export const PROJECT_STATUSES_SELECTABLE: readonly ProjectStatus[] = ['PREPARATION', 'ACTIVE', 'COMPLETED']

/**
 * Detailblatt 1 3.1, Spalte „Buchbar in der Zeiterfassung". Fertiggestellt
 * bleibt buchbar — Nacharbeit und Restarbeiten kommen nach dem Termin.
 */
export const PROJECT_STATUSES_BOOKABLE: readonly ProjectStatus[] = ['ACTIVE', 'COMPLETED']

/** Offene Projekte — für Dashboard, Ampeln und die Vorgabe der Liste. */
export const PROJECT_STATUSES_OPEN: readonly ProjectStatus[] = ['PREPARATION', 'ACTIVE']

/** Detailblatt 4 6.2 — Positionen, auf die gebucht werden darf (Vorgabe von `mirrorStatuses`). */
export const PROJECT_POSITION_STATUSES_BOOKABLE: readonly ProjectPositionStatus[] = ['RELEASED', 'COMPLETED']

/** Detailblatt 4 6.2 — Arbeitsschritte, auf die gebucht werden darf. */
export const PROJECT_WORK_STEP_STATUSES_BOOKABLE: readonly ProjectWorkStepStatus[] = ['READY', 'ASSIGNED', 'IN_PROGRESS']

/** Freigegeben und noch nicht fertig — freigegeben, zugeteilt oder in Arbeit (Nachtrag 9). */
export const PROJECT_WORK_STEP_STATUSES_RELEASED: readonly ProjectWorkStepStatus[] = ['READY', 'ASSIGNED', 'IN_PROGRESS']

// ── Lebenslauf: erlaubte Übergänge (Detailblatt 1 3.2) ───────────────────────

/**
 * Was von hier aus **von Hand** erreichbar ist. Alles, was nicht in dieser
 * Tabelle steht, ist verboten (`409 PROJECT_STATUS_TRANSITION`).
 *
 * Automatische Übergänge (Beleg festgeschrieben, erste Freigabe, erste Stunde,
 * Archivlauf) sind eine Teilmenge davon: Sie gehen nie rückwärts und nie über
 * `IN_PROGRESS` hinaus — fertig und abgerechnet erklärt ein Mensch.
 */
export const PROJECT_STATUS_TRANSITIONS: Readonly<Record<ProjectStatus, readonly ProjectStatus[]>> = {
  PREPARATION: ['ACTIVE', 'CANCELLED'],
  ACTIVE: ['PREPARATION', 'COMPLETED', 'CANCELLED'],
  COMPLETED: ['ACTIVE', 'CANCELLED'],
  CANCELLED: ['PREPARATION', 'ACTIVE'],
}

/** Anfangsstatus beim Anlegen (Detailblatt 1 3.2, Vorgabe aus `ProjectSetting.defaultStatus`). */
export const PROJECT_INITIAL_STATUSES: readonly ProjectStatus[] = ['PREPARATION', 'ACTIVE']

/**
 * Wo eine **Begründung Pflicht** ist (Detailblatt 1 3.2): beim Stornieren und
 * bei jedem Wiederöffnen (aus Fertiggestellt oder Storniert). Ein Statuswechsel,
 * der erklärt werden muss, wird nicht kommentarlos protokolliert.
 */
export function projectStatusReasonRequired(von: ProjectStatus, nach: ProjectStatus): boolean {
  if (nach === 'CANCELLED') return true
  if (von === 'COMPLETED' || von === 'CANCELLED') return true
  return false
}

/** Ist dieser Übergang erlaubt? */
export function projectStatusAllowed(von: ProjectStatus, nach: ProjectStatus): boolean {
  return PROJECT_STATUS_TRANSITIONS[von].includes(nach)
}

// ── Abrechnungsmodalitäten (Detailblatt 3 7.1) ───────────────────────────────

/**
 * Die drei Schemata (Auftraggeber 18.9.2026). Der **Zahlungsplan ist kein
 * Schema mehr, sondern ein Intervall** von Pauschal und Einheitspreis. Der Wert
 * `PAYMENT_PLAN` bleibt im Prisma-Enum stehen (die Migration
 * `ap35n_abrechnungsmodalitaeten` hat alle Datensätze überführt), wird aber
 * weder angeboten noch angenommen — dasselbe gilt für das Intervall `TRIWEEKLY`.
 */
export const PROJECT_BILLING_SCHEMES = ['TIME_AND_MATERIAL', 'LUMP_SUM', 'UNIT_PRICE'] as const
export type ProjectBillingScheme = (typeof PROJECT_BILLING_SCHEMES)[number]

/** Kalender-Intervalle der Regie — `ANY` heißt „beliebig", ohne Stichtag. */
export const PROJECT_BILLING_INTERVALS_REGIE = [
  'DAILY',
  'WEEKLY',
  'BIWEEKLY',
  'MONTHLY',
  'BIMONTHLY',
  'QUARTERLY',
  'HALF_YEARLY',
  'YEARLY',
  'ANY',
] as const

/** Ereignis-Intervalle von Pauschal und Einheitspreis. */
export const PROJECT_BILLING_INTERVALS_EVENT = ['ON_COMPLETION', 'PER_POSITION', 'PAYMENT_PLAN', 'ADVANCE'] as const

export const PROJECT_BILLING_INTERVALS = [...PROJECT_BILLING_INTERVALS_EVENT, ...PROJECT_BILLING_INTERVALS_REGIE] as const
export type ProjectBillingInterval = (typeof PROJECT_BILLING_INTERVALS)[number]

/**
 * **Die eine Stelle**, an der Schema und Intervall zusammenhängen (Auftraggeber
 * 18.9.2026): Regie rechnet nach dem Kalender ab, Pauschal und Einheitspreis
 * nach einem Ereignis. Backend (`PROJECT_BILLING_INTERVAL_MISMATCH`) und
 * Masken lesen dieselbe Tabelle.
 */
export const PROJECT_BILLING_INTERVALS_BY_SCHEME: Readonly<
  Record<ProjectBillingScheme, readonly ProjectBillingInterval[]>
> = {
  TIME_AND_MATERIAL: PROJECT_BILLING_INTERVALS_REGIE,
  LUMP_SUM: PROJECT_BILLING_INTERVALS_EVENT,
  UNIT_PRICE: PROJECT_BILLING_INTERVALS_EVENT,
}

/**
 * Die Intervalle, die zu einem Schema passen — in der Reihenfolge der Auswahl;
 * das erste ist der Rückfall, wenn ein Schemawechsel das Intervall ungültig
 * macht. Ein unbekanntes oder leeres Schema liefert eine leere Liste.
 */
export function billingIntervalsFor(scheme: string | null | undefined): readonly ProjectBillingInterval[] {
  if (!scheme) return []
  return PROJECT_BILLING_INTERVALS_BY_SCHEME[scheme as ProjectBillingScheme] ?? []
}

/** Passt das Intervall zum Schema? */
export function billingIntervalAllowed(scheme: string | null | undefined, interval: string | null | undefined): boolean {
  if (!interval) return false
  return (billingIntervalsFor(scheme) as readonly string[]).includes(interval)
}

/** Detailblatt 1 2.5 — woher ein Projekt kommt. */
export const PROJECT_ORIGINS = ['MANUAL', 'DOCUMENT', 'CALCULATION', 'LV_IMPORT', 'COPY', 'TIME_TRACKING'] as const
export type ProjectOrigin = (typeof PROJECT_ORIGINS)[number]

/**
 * Die Felder, die das Projekt an seine Positionen vererbt (Detailblatt 2 2.2).
 * Beim Lesen aufgelöst, nie kopiert — deshalb steht die Liste an **einer** Stelle.
 */
export const PROJECT_INHERITED_FIELDS = [
  'customerOrderRef',
  'customerOrderNumber',
  'customerOrderPosition',
  'siteAddressId',
  'siteAddressJson',
  'billingScheme',
  'billingInterval',
  'billingNotes',
] as const
export type ProjectInheritedField = (typeof PROJECT_INHERITED_FIELDS)[number]

/** Ein vererbbarer Wert, wie ihn der Server liefert (Detailblatt 2 5.2). */
export interface InheritedValue<T> {
  own: T | null
  effective: T | null
  inherited: boolean
}

// ── Lebenslauf der Position (Detailblatt 2 4.1) ──────────────────────────────

/**
 * Was von einem Positionsstatus aus erreichbar ist. Alles, was hier nicht
 * steht, ist verboten (`409 POSITION_STATUS_TRANSITION`).
 *
 * Die **Ausführungsfreigabe ist der Übergang** nach `RELEASED` (Blatt 2 14.2-2)
 * und läuft über `…/release`, weil sie eine Freigabe trägt. Zurück nach
 * `PREPARATION` ist seit Nachtrag 7 ein gewöhnlicher Statuswechsel, der die
 * Freigabe dabei widerruft — einen eigenen Knopf „Freigabe widerrufen" gibt es
 * nicht mehr.
 */
export const PROJECT_POSITION_STATUS_TRANSITIONS: Readonly<
  Record<ProjectPositionStatus, readonly ProjectPositionStatus[]>
> = {
  PREPARATION: ['RELEASED', 'BLOCKED', 'CANCELLED'],
  RELEASED: ['PREPARATION', 'BLOCKED', 'COMPLETED', 'CANCELLED'],
  BLOCKED: ['PREPARATION', 'RELEASED', 'CANCELLED'],
  COMPLETED: ['RELEASED', 'CANCELLED'],
  CANCELLED: ['PREPARATION'],
}

export function positionStatusAllowed(von: ProjectPositionStatus, nach: ProjectPositionStatus): boolean {
  return PROJECT_POSITION_STATUS_TRANSITIONS[von].includes(nach)
}

/**
 * Wo eine Begründung Pflicht ist (Blatt 2 4.1): Sperren, Stornieren,
 * Nacharbeit (aus Fertiggestellt zurück) und Wiederöffnen. Zurück in die
 * Vorbereitung braucht keine (Nachtrag 7).
 */
export function positionStatusReasonRequired(von: ProjectPositionStatus, nach: ProjectPositionStatus): boolean {
  if (nach === 'BLOCKED' || nach === 'CANCELLED') return true
  if (von === 'COMPLETED' || von === 'CANCELLED') return true
  return false
}

/** Status, in denen eine Position nicht mehr bearbeitet wird (Blatt 2 11.). */
export const PROJECT_POSITION_STATUSES_READONLY: readonly ProjectPositionStatus[] = ['CANCELLED']

// ── Lebenslauf des Arbeitsschritts (Detailblatt 4 4.) ────────────────────────

export const PROJECT_WORK_STEP_STATUS_TRANSITIONS: Readonly<
  Record<ProjectWorkStepStatus, readonly ProjectWorkStepStatus[]>
> = {
  PLANNED: ['READY', 'CANCELLED'],
  READY: ['PLANNED', 'ASSIGNED', 'IN_PROGRESS', 'PAUSED', 'DONE', 'CANCELLED'],
  ASSIGNED: ['PLANNED', 'IN_PROGRESS', 'PAUSED', 'DONE', 'CANCELLED'],
  IN_PROGRESS: ['PAUSED', 'DONE', 'CANCELLED'],
  PAUSED: ['READY', 'ASSIGNED', 'IN_PROGRESS', 'CANCELLED'],
  DONE: ['IN_PROGRESS'],
  CANCELLED: [],
}

export function workStepStatusAllowed(von: ProjectWorkStepStatus, nach: ProjectWorkStepStatus): boolean {
  return PROJECT_WORK_STEP_STATUS_TRANSITIONS[von].includes(nach)
}

export function workStepStatusReasonRequired(von: ProjectWorkStepStatus, nach: ProjectWorkStepStatus): boolean {
  if (nach === 'PAUSED' || nach === 'CANCELLED') return true
  if (von === 'DONE' && nach === 'IN_PROGRESS') return true
  return false
}

/** Positionen, an denen ein Arbeitsschritt freigegeben werden darf (Blatt 4 4.). */
export const PROJECT_POSITION_STATUSES_RELEASED: readonly ProjectPositionStatus[] = ['RELEASED']

/** Blatt 4 2. — Soll gesamt oder je Mengeneinheit. */
export const PROJECT_QUANTITY_BASES = ['TOTAL', 'PER_UNIT'] as const
export type ProjectQuantityBasis = (typeof PROJECT_QUANTITY_BASES)[number]

// ── Leistungsstand (Detailblatt 2 6.) ────────────────────────────────────────

export interface WorkStepProgressInput {
  executionStatus: ProjectWorkStepStatus
  plannedHours: number | null
  quantityBasis: ProjectQuantityBasis
}

export interface ProgressResult {
  /** 0–100, gerundet. */
  percent: number
  source: 'MANUAL' | 'WORK_STEPS' | 'STATUS' | 'NONE'
  /** Gewicht für das Projektmittel: Soll-Stunden der Position, sonst 0. */
  plannedHours: number
  /** Übersetzungsschlüssel eines Hinweises, wenn geschätzt wurde. */
  note?: string
}

/**
 * Der Leistungsstand **einer Position** (Blatt 2 6.).
 *
 * Ein Handwert schlägt die Rechnung; ohne Arbeitsschritte erklärt der Status,
 * wie weit es ist — und sagt dazu, dass er schätzt. Die Verfeinerung aus Blatt 8
 * 4.3 (`IN_PROGRESS` mit Ist ÷ Soll) braucht Ist-Stunden und kommt mit dem
 * Baustein `.CONTROLLING`; bis dahin zählt nur `DONE`, wie Blatt 2 es schreibt.
 */
export function berechneLeistungsstand(
  position: { status: ProjectPositionStatus; progressPercent: number | null; quantity: number },
  schritte: readonly WorkStepProgressInput[],
): ProgressResult {
  const aktiv = schritte.filter((s) => s.executionStatus !== 'CANCELLED')
  const soll = (s: WorkStepProgressInput) =>
    (s.plannedHours ?? 0) * (s.quantityBasis === 'PER_UNIT' ? position.quantity : 1)
  const sollGesamt = aktiv.reduce((summe, s) => summe + soll(s), 0)

  if (position.status === 'CANCELLED') return { percent: 0, source: 'STATUS', plannedHours: 0 }
  if (position.progressPercent != null) {
    return { percent: position.progressPercent, source: 'MANUAL', plannedHours: sollGesamt }
  }
  // `COMPLETED` setzt den **effektiven** Wert auf 100 %, ohne den Handwert zu
  // ändern — deshalb steht diese Prüfung hinter dem Handwert, nicht davor.
  if (position.status === 'COMPLETED') {
    return { percent: 100, source: 'STATUS', plannedHours: sollGesamt }
  }
  // Ohne Arbeitsschritte und ohne Handwert gibt es nichts zu rechnen. Bis
  // Nachtrag 7 schätzte „in Arbeit" hier 50 % — diesen Status gibt es nicht mehr.
  if (aktiv.length === 0) return { percent: 0, source: 'STATUS', plannedHours: 0 }
  if (sollGesamt === 0) {
    const fertig = aktiv.filter((s) => s.executionStatus === 'DONE').length
    return { percent: Math.round((fertig / aktiv.length) * 100), source: 'WORK_STEPS', plannedHours: 0 }
  }
  const fertigStunden = aktiv.filter((s) => s.executionStatus === 'DONE').reduce((summe, s) => summe + soll(s), 0)
  return { percent: Math.round((fertigStunden / sollGesamt) * 100), source: 'WORK_STEPS', plannedHours: sollGesamt }
}

/** Das Projektmittel: gewichtet nach Soll-Stunden, sonst ungewichtet (Blatt 2 6.). */
export function leistungsstandDesProjekts(positionen: readonly ProgressResult[]): number {
  if (positionen.length === 0) return 0
  const gewicht = positionen.reduce((summe, p) => summe + p.plannedHours, 0)
  if (gewicht > 0) {
    return Math.round(positionen.reduce((summe, p) => summe + p.percent * p.plannedHours, 0) / gewicht)
  }
  return Math.round(positionen.reduce((summe, p) => summe + p.percent, 0) / positionen.length)
}

// ── Dateien, Notizen, Freigaben (Blatt 6 4.1, Blatt 9 2.2, AP-35 4.15) ───────

/** `ORDER`: Auftragsdokument am Projekt (Nachtrag 8), abgelegt unter „Kaufmännisches“. */
export const PROJECT_FILE_KINDS = ['DRAWING', 'PLAN', 'PHOTO', 'OFFER', 'CORRESPONDENCE', 'ORDER', 'LV', 'OTHER'] as const
export type ProjectFileKind = (typeof PROJECT_FILE_KINDS)[number]

export const PROJECT_NOTE_COLORS = ['YELLOW', 'BLUE', 'GREEN', 'PINK', 'GREY'] as const
export type ProjectNoteColor = (typeof PROJECT_NOTE_COLORS)[number]

export const PROJECT_ENTITY_TYPES = [
  'PROJECT',
  'POSITION',
  'WORK_STEP',
  'BOM_LINE',
  'DECLARATION',
  'JOURNAL',
  'CERTIFICATE',
  'IMPORT_BATCH',
] as const
export type ProjectEntityType = (typeof PROJECT_ENTITY_TYPES)[number]

export const PROJECT_APPROVAL_KINDS = [
  'EXECUTION',
  'WORKSTEP_RELEASE',
  'INQUIRY',
  'ORDER',
  'BILLING_DONE',
  'DECLARATION_ISSUED',
  'SUPPLEMENT_APPROVED',
  'STATUS_CHANGE',
  'HEAD_CHANGE',
  /** RN-4 (AP-47 7.2): Abnahme durch den Kunden im Montageprotokoll. */
  'ACCEPTANCE',
] as const
export type ProjectApprovalKind = (typeof PROJECT_APPROVAL_KINDS)[number]

/**
 * Detailblatt 1 3.4 — der Status eines **Sammelauftrags** aus den Status seiner
 * Positionen. Eine Tabelle, geprüft in `projects.spec.ts`; der Dienst setzt sie
 * nach jeder Positionsänderung (`sammelStatusNachziehen`).
 */
export function sammelauftragStatus(positionen: readonly string[]): ProjectStatus {
  if (positionen.length === 0) return 'ACTIVE'
  const nur = (erlaubt: readonly string[]) => positionen.every((s) => erlaubt.includes(s))
  // Fertig ist der Sammelauftrag, wenn jede nicht stornierte Position fertiggestellt ist.
  if (nur(['COMPLETED', 'CANCELLED']) && positionen.some((s) => s === 'COMPLETED')) return 'COMPLETED'
  return 'ACTIVE'
}

/** Aufgaben-Zustand, abgeleitet (Blatt 9 4.1) — nie gespeichert. */
export type ProjectTaskState = 'OPEN' | 'DUE_TODAY' | 'OVERDUE' | 'DONE'

/** Die drei Ampeln (Blatt 8 4.). Grau heißt „keine Quelle", nicht „in Ordnung". */
export type TrafficLight = 'GREEN' | 'YELLOW' | 'RED' | 'GREY'

export interface TrafficLightResult {
  value: TrafficLight
  /** Übersetzungsschlüssel der Begründung (`projects.trafficLights.*`). */
  reason: string
}

// ── Auftrag je Arbeitsschritt (AP-35 Nachtrag 9) ─────────────────────────────

/**
 * Vorgaben für Überschrift und Unterschriftzeilen des Auftrags, solange die
 * Kategorie (Termin, `ProjectPhase`) nichts Eigenes trägt. Montage und
 * Produktion nach den Mustern des Auftraggebers (19.9.2026); jede andere
 * Kategorie druckt einen allgemeinen Arbeitsauftrag.
 */
export function workOrderDefaults(phase: { key: string; isAssembly: boolean }): { title: string; signatureLines: string[] } {
  // „Fertiggestellt am“ mit der Unterschrift des Ausführenden druckt jeder Auftrag
  // unter diesen Zeilen (Nachtrag 10) — deshalb steht „Fertiggestellt von“ hier nicht mehr.
  if (phase.isAssembly) return { title: 'Montageauftrag', signatureLines: ['Montiert von'] }
  if (phase.key === 'PRODUCTION') {
    return { title: 'Produktionsauftrag', signatureLines: ['Produziert von', 'Geschweißt von', 'Oberfläche'] }
  }
  return { title: 'Arbeitsauftrag', signatureLines: ['Ausgeführt von'] }
}

/** Überschrift und Unterschriftzeilen, wie sie gedruckt werden — Eigenes vor Vorgabe. */
export function workOrderLayout(phase: {
  key: string
  isAssembly: boolean
  orderTitle?: string | null
  signatureLines?: unknown
}): { title: string; signatureLines: string[] } {
  const vorgabe = workOrderDefaults(phase)
  const zeilen = Array.isArray(phase.signatureLines)
    ? phase.signatureLines.filter((z): z is string => typeof z === 'string' && z.trim() !== '').map((z) => z.trim())
    : null
  return { title: phase.orderTitle?.trim() || vorgabe.title, signatureLines: zeilen ?? vorgabe.signatureLines }
}

// ── Unterschriften des Auftrags, je Mandant (AP-35 Nachtrag 10) ──────────────

/** Woher eine Zeile der Freigabe ihren Namen vorbelegt: die Projektrolle eines Personenfelds im Technik-Blatt. */
export const WORK_ORDER_NAME_SOURCES = ['WELDING_COORDINATOR', 'FPC'] as const
export type WorkOrderNameSource = (typeof WORK_ORDER_NAME_SOURCES)[number]
export const WORK_ORDER_NAME_SOURCE_LABELS: Record<WorkOrderNameSource, string> = {
  WELDING_COORDINATOR: 'Schweißaufsichtsperson (SAP)',
  FPC: 'Werkseigene Produktionskontrolle (WPK)',
}

export interface WorkOrderSignatureLine {
  label: string
  /** Name vorbelegen aus dem Technik-Blatt; `null` = leer zum Ausfüllen. */
  nameFrom: WorkOrderNameSource | null
}

/**
 * Die festen Unterschriften des Auftrags, die jeder Betrieb selbst festlegt
 * (Auftraggeber 19.9.2026): die Abschlusszeile unter den Zeilen der Kategorie
 * und der Abschnitt „Freigabe nach EN 1090“, der nur bei gesetzter
 * Ausführungsklasse erscheint.
 */
export interface WorkOrderSignatures {
  /** „Fertiggestellt“ — mit Name des Ausführenden, Datum und Unterschrift; leer = keine Zeile. */
  closingLine: string | null
  en1090: { enabled: boolean; title: string; lines: WorkOrderSignatureLine[] }
}

export const WORK_ORDER_SIGNATURES_DEFAULT: WorkOrderSignatures = {
  closingLine: 'Fertiggestellt',
  en1090: {
    enabled: true,
    title: 'Freigabe nach EN 1090',
    lines: [
      { label: 'Schweißaufsicht (SAP)', nameFrom: 'WELDING_COORDINATOR' },
      { label: 'WPK', nameFrom: 'FPC' },
    ],
  },
}

/**
 * Liest die gespeicherte Einstellung und füllt, was fehlt, mit der Vorgabe —
 * `null` in der Datenbank heißt „Vorgabe“. Unbrauchbares fällt weg, statt den
 * Ausdruck scheitern zu lassen. Dient dem Server beim Speichern und Drucken
 * und der Maske beim Anzeigen.
 */
export function workOrderSignatures(roh: unknown): WorkOrderSignatures {
  const v = WORK_ORDER_SIGNATURES_DEFAULT
  if (!roh || typeof roh !== 'object') return JSON.parse(JSON.stringify(v)) as WorkOrderSignatures
  const r = roh as { closingLine?: unknown; en1090?: { enabled?: unknown; title?: unknown; lines?: unknown } }
  const text = (x: unknown, max: number) => (typeof x === 'string' ? x.trim().slice(0, max) : '')
  const zeilen = Array.isArray(r.en1090?.lines)
    ? (r.en1090!.lines as unknown[])
        .map((z) => {
          const o = (z ?? {}) as { label?: unknown; nameFrom?: unknown }
          const nameFrom = (WORK_ORDER_NAME_SOURCES as readonly unknown[]).includes(o.nameFrom) ? (o.nameFrom as WorkOrderNameSource) : null
          return { label: text(o.label, 80), nameFrom }
        })
        .filter((z) => z.label)
        .slice(0, 12)
    : v.en1090.lines
  return {
    closingLine: r.closingLine === undefined ? v.closingLine : text(r.closingLine, 80) || null,
    en1090: {
      enabled: typeof r.en1090?.enabled === 'boolean' ? r.en1090.enabled : v.en1090.enabled,
      title: text(r.en1090?.title, 80) || v.en1090.title,
      lines: zeilen,
    },
  }
}
